import { LogoMark } from './Logo'
import type { PromptAnalysis } from '@/lib/api'

function scoreColor(score: number) {
  if (score >= 85) return { dot: 'bg-emerald-400', text: 'text-emerald-300', ring: 'border-emerald-700' }
  if (score >= 60) return { dot: 'bg-primary-400', text: 'text-primary-300', ring: 'border-primary-800' }
  if (score >= 35) return { dot: 'bg-amber-400', text: 'text-amber-300', ring: 'border-amber-800' }
  return { dot: 'bg-rose-400', text: 'text-rose-300', ring: 'border-rose-800' }
}

interface Props {
  analysis: PromptAnalysis
  /** When provided, the badge is a clickable toggle (with a chevron). Omit for a static score pill. */
  open?: boolean
  onToggle?: () => void
}

// Completeness pill that leads with the Promptioso mark (free rule-layer score). Acts as a
// toggle when onToggle is given, otherwise renders as a static indicator.
export default function ScoreBadge({ analysis, open, onToggle }: Props) {
  const c = scoreColor(analysis.score)
  const count = analysis.hints.length
  const Tag = onToggle ? 'button' : 'span'
  return (
    <Tag
      onClick={onToggle}
      aria-expanded={onToggle ? open : undefined}
      title={`Prompt completeness ${analysis.score}/100`}
      className={`inline-flex items-center gap-1.5 rounded-full border ${c.ring} bg-gray-900 py-1 pl-1.5 pr-2.5 text-xs font-medium ${c.text} ${onToggle ? 'hover:bg-gray-800' : ''}`}
    >
      <LogoMark size="sm" className="h-4 w-4 shrink-0" />
      <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
      <span className="tabular-nums">{analysis.score}</span>
      <span>{analysis.label}</span>
      {analysis.ready && <span className="font-semibold text-emerald-300" title="Generate-ready: essentials covered, no contradictions">✓</span>}
      {count > 0 && (
        <span className="rounded-full bg-gray-800 px-1.5 py-0.5 text-[10px] text-gray-400">
          {count} tip{count === 1 ? '' : 's'}
        </span>
      )}
      {onToggle && <span className="text-gray-500">{open ? '▴' : '▾'}</span>}
    </Tag>
  )
}
